/**
 * Application form constants (extracted from
 * "FORMULAIRE de RENSEIGNEMENTS en vue d'INSCRIPTION" PDF).
 *
 * Kept in a separate module to keep ApplicationPage.js readable.
 */
import {
  Sparkles, Search, Briefcase, Target, User, CreditCard,
  Share2, MessageCircle, Newspaper, Compass, GraduationCap, Rocket,
  Star, Trophy, Heart, ShieldCheck, FileText, Award,
  Instagram, Facebook, Youtube, Linkedin, Globe,
} from 'lucide-react';

export const STEPS = [
  { id: 1, name: 'Démarrage',   icon: Sparkles,   xp: 0   },
  { id: 2, name: 'Découverte',  icon: Search,     xp: 50  },
  { id: 3, name: 'Situation',   icon: Briefcase,  xp: 100 },
  { id: 4, name: 'Coordonnées', icon: User,       xp: 150 },
  { id: 5, name: 'Formation',   icon: Target,     xp: 200 },
  { id: 6, name: 'Financement', icon: CreditCard, xp: 250 },
  { id: 7, name: 'Validation',  icon: FileText,   xp: 300 },
];

// Section 1 — Comment nous as-tu connus ? (multi-select)
export const discoveryChannels = [
  { value: 'site_internet',   label: 'Site internet',          desc: 'sellerie-academie.fr',                icon: Globe },
  { value: 'reseau_sts',      label: 'Réseau STS',             desc: 'La Sellerie Tout Simplement',          icon: Sparkles },
  { value: 'youtube',         label: 'Chaîne YouTube',         desc: '@sts.lasellerie-tout-simplement',      icon: Youtube },
  { value: 'facebook',        label: 'Facebook',               desc: 'Page officielle',                       icon: Facebook },
  { value: 'instagram',       label: 'Instagram',              desc: 'Stories & Reels',                       icon: Instagram },
  { value: 'linkedin',        label: 'LinkedIn',               desc: 'Réseau pro',                            icon: Linkedin },
  { value: 'deja_venu',       label: 'Déjà venu·e en formation', desc: 'Ancien·ne élève',                     icon: Trophy },
  { value: 'recommandation',  label: "Recommandation d'un·e ancien·ne élève", desc: 'Bouche-à-oreille',     icon: MessageCircle },
  { value: 'presse',          label: 'Presse / Article',       desc: 'Magazine, blog, podcast',               icon: Newspaper },
  { value: 'autre',           label: 'Autre',                  desc: 'Précisez ci-dessous',                   icon: Star },
];

// Section 2 — Coordonnées
export const particularitiesOptions = [
  { value: 'plus_30km',       label: "J'habite à plus de 30 km d'Istres" },
  { value: 'enfants_moins_10', label: 'Enfant(s) de moins de 10 ans à charge' },
  { value: 'ras',             label: 'RAS — Néant' },
];

// Section 3 — Situation professionnelle
export const educationLevels = [
  { value: 'bac3_4',        label: 'Bac+3 / Bac+4' },
  { value: 'bac2',          label: 'Bac+2' },
  { value: 'bac_btn_bt_bp', label: 'BAC · BTN · BT · BP' },
  { value: 'bepc_bep_cap',  label: 'BEPC · BEP · CAP' },
  { value: 'cep_ses',       label: 'CEP · SES' },
];

export const professionalSituations = [
  { value: 'gerant',          label: 'Gérant·e société',          desc: 'SARL, SAS, EURL…',                icon: Briefcase, color: 'from-blue-500/20 to-blue-700/10' },
  { value: 'auto_entrepreneur', label: 'Auto-entrepreneur',        desc: 'Micro-entreprise / artisan',      icon: Rocket,    color: 'from-amber-500/20 to-amber-700/10' },
  { value: 'creation',        label: 'En cours de création',      desc: 'Projet en cours',                  icon: Target,    color: 'from-pink-500/20 to-pink-700/10' },
  { value: 'salarie',         label: 'Salarié·e',                 desc: 'CDI / CDD',                         icon: User,      color: 'from-purple-500/20 to-purple-700/10' },
  { value: 'interimaire',     label: 'Intérimaire',                desc: 'Missions ponctuelles',             icon: Compass,   color: 'from-indigo-500/20 to-indigo-700/10' },
  { value: 'demandeur_emploi', label: "Demandeur d'emploi",       desc: 'Inscrit·e à France Travail',       icon: Search,    color: 'from-green-500/20 to-green-700/10' },
  { value: 'retraite',        label: 'Retraité·e',                 desc: '',                                  icon: ShieldCheck, color: 'from-slate-500/20 to-slate-700/10' },
];

// Section 4 — Information formation
export const targetFormations = [
  { value: 'decouverte', label: 'DÉCOUVERTE du métier de Sellier', desc: '3 jours' },
  { value: 'generale',   label: 'Formation GÉNÉRALE à la SELLERIE', desc: '23 jours' },
  { value: 'nautique',   label: 'Formation SELLERIE NAUTIQUE',      desc: '23 jours' },
  { value: 'auto',       label: 'Formation SELLERIE AUTO',          desc: '23 jours' },
  { value: 'moto',       label: 'Formation SELLERIE MOTO',          desc: '23 jours' },
  { value: 'habitat',    label: 'Formation SELLERIE HABITAT & MÉDICAL', desc: '23 jours' },
  { value: 'dessin',     label: 'Module DESSIN TECHNIQUE',          desc: '1 jour' },
];

export const handednessOptions = [
  { value: 'droitier', label: 'Droitier·e', icon: User },
  { value: 'gaucher',  label: 'Gaucher·e',  icon: User },
];

export const sewingLevels = [
  { value: 'debutant',  label: 'Débutant·e',  desc: "Je découvre la machine à coudre industrielle",     icon: Sparkles },
  { value: 'confirme',  label: 'Confirmé·e',  desc: 'Je sais déjà utiliser une machine à coudre indus.', icon: Award },
];

export const motivationTagsOptions = [
  { value: 'renforcer',     label: 'Renforcer mes connaissances' },
  { value: 'nouvelles',     label: 'Acquérir de nouvelles compétences' },
  { value: 'creation',      label: 'Projet de création de mon entreprise' },
  { value: 'entreprise',    label: 'Formation prévue par mon entreprise' },
];

// Section 5 — Financement & accessibilité
export const fundingOptions = [
  { value: 'autofinancement', label: 'AUTOFINANCEMENT' },
  { value: 'ptp',             label: 'Projet Transition Professionnelle (salarié)' },
  { value: 'agefiph',         label: 'AGEFIPH (travailleur handicapé)' },
  { value: 'region',          label: 'Région' },
  { value: 'opco',            label: 'OPCO de mon entreprise (salarié)' },
  { value: 'faf',             label: 'FAF (autoentrepreneur)' },
  { value: 'france_travail',  label: "France Travail (demandeur d'emploi)" },
  { value: 'autre',           label: 'Autre' },
];

// Carry-over from previous gamified form (compact discovery source — used in
// the optional "comment as-tu connu Sellerie Académie ?" simple radio).
export const goals = [
  { value: 'main_activity', label: 'Mon nouveau métier',     desc: 'La sellerie sera mon activité principale', icon: Trophy },
  { value: 'secondary',     label: 'Activité complémentaire', desc: "En parallèle d'une autre activité",        icon: Star },
  { value: 'passion',       label: 'Passion personnelle',     desc: 'Pour le plaisir et le savoir-faire',       icon: Heart },
  { value: 'exploring',     label: "J'explore encore",        desc: 'Je découvre, on verra bien',                icon: Compass },
];
